import React from "react";
import { motion } from "framer-motion";

const ABOUT_IMG =
  "https://static.prod-images.emergentagent.com/jobs/39e5a5b9-5e3d-485e-a045-ea871eda4997/images/2477a2553e839ef2adb4d9c5e10539988a7938aa01d23d237e80332eb307e0b6.png";

export default function About() {
  return (
    <section id="about" data-testid="about-section" className="py-14 md:py-20 bg-paper">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex items-center gap-4 mb-8 md:mb-12">
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-signal">§ 01 — About</span>
          <span className="flex-1 h-px bg-line" />
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink2">Profile</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
          {/* Big story block */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, ease: [0.2, 0.7, 0.2, 1] }}
            className="md:col-span-8 border border-ink p-8 md:p-12 bg-paper"
          >
            <h2 className="font-heading font-black text-4xl md:text-6xl leading-[0.95] tracking-tightest mb-8">
              Fourteen years of turning{" "}
              <span className="text-signal">marketing spend</span> into measurable pipeline.
            </h2>
            <div className="grid md:grid-cols-2 gap-8 text-[15px] md:text-base leading-relaxed text-ink2">
              <p>
                I&apos;m a self-driven digital marketing consultant with <strong className="text-ink">14+ years</strong>{" "}
                of deep B2B high-tech experience — shipped demand programs for{" "}
                <strong className="text-ink">cybersecurity, DevTools, MarTech and enterprise SaaS</strong> brands.
                I architect demand generation, growth and performance marketing systems that scale, with the
                discipline to manage million-dollar budgets cleanly.
              </p>
              <p>
                Fluent across the full stack — marketing automation (HubSpot, Marketo), CRM (Salesforce), ABM
                (6sense, Rollworks), PPC (LinkedIn, Google, Meta, Bing) — and increasingly the AI-native layer:{" "}
                <strong className="text-ink">Claude, GPT-5, MCP servers, Perplexity, Cursor</strong> and agentic
                workflows that actually ship output instead of demos.
              </p>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 border-t border-ink" data-testid="about-pillars">
              {[
                ["01", "Pipeline architecture", "Demand gen, ABM and lifecycle programs measured on pipeline, not clicks."],
                ["02", "Paid media discipline", "LinkedIn, Google, Meta and Bing — structure, tracking and budgets run cleanly."],
                ["03", "AI-native operations", "Claude, GPT-5 and MCP-driven workflows that ship real output."],
              ].map(([n, t, d], i) => (
                <div key={n} className={`pt-6 pb-2 sm:pr-6 ${i > 0 ? "sm:pl-6 sm:border-l border-line" : ""}`}>
                  <span className="font-mono text-[11px] text-signal">{n}</span>
                  <h3 className="mt-2 font-heading font-bold text-lg leading-tight">{t}</h3>
                  <p className="mt-2 text-[14px] text-ink2 leading-relaxed">{d}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              {[
                "Demand Gen",
                "Growth",
                "Performance",
                "ABM",
                "PPC",
                "SEO",
                "CRM Ops",
                "Lifecycle",
                "AI Marketing",
                "Agentic Workflows",
                "MCP Ops",
                "Cybersecurity",
              ].map((t) => (
                <span
                  key={t}
                  className="font-mono text-[11px] uppercase tracking-[0.18em] border border-ink px-3 py-2"
                >
                  {t}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Portrait/concept image */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.2, 0.7, 0.2, 1] }}
            className="md:col-span-4 flex flex-col gap-6"
          >
            <div className="relative aspect-[3/4] border border-ink overflow-hidden">
              <img
                src={ABOUT_IMG}
                alt="Performance marketing tools overview"
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 left-4 bg-paper text-ink px-2 py-1 font-mono text-[10px] uppercase tracking-[0.2em]">
                Exhibit A
              </div>
            </div>

            <div className="bg-ink text-paper p-8 border border-ink">
              <div className="font-heading font-black text-6xl leading-none tracking-tightest number-tabular">
                14+
              </div>
              <div className="mt-3 font-mono text-[11px] uppercase tracking-[0.22em] text-paper/70">
                Years in B2B<br />high-tech marketing
              </div>
              <a
                href="https://www.linkedin.com/in/abhinavdhiman/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-linkedin-link"
                className="mt-8 inline-flex items-center gap-2 border-b border-paper pb-1 font-mono text-[11px] uppercase tracking-[0.22em] hover:text-signal hover:border-signal"
              >
                Connect on LinkedIn →
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
